import React, { useCallback, useMemo, useState } from "react"

import { addDays, format, startOfDay } from "date-fns"
import { Truck } from "lucide-react"
import { DateRange } from "react-day-picker"
import { toast } from "sonner"

import CostBreakdownCard from "@/components/shared/CostBreakdownCard"
import { EmptyState } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { useAuth } from "@/contexts/AuthContext"
import {
  useCollectionsConfig,
  useLocations,
  useRouteInstancesForDateRange,
  useRoutesPlanned,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { buildCsv, downloadCsv } from "@/lib/csvExport"
import { buildHaulagePlanTable } from "@/lib/haulagePlanExport"
import { isHaulageDesignatedRoute } from "@/lib/haulageUtils"
import {
  buildPlanningListTable,
  exportDateRangeSlug,
  formatShiftCoverCell,
} from "@/lib/planningListExport"
import {
  expandPlannedRoutes,
  getRouteSortValue,
  sortDayRoutes,
  toPlannedRoute,
} from "@/lib/planningRoutes"
import { resolveRouteKind } from "@/lib/routeCostCalculation"
import {
  buildRunList,
  isRunListRoute,
  runListFilename,
  runListVolume,
} from "@/lib/runListExport"
import { coverRank } from "@/lib/shiftCover"
import { Location, PlannedRoute, RouteKind } from "@/types/collections"

import PlanningDateGroup from "./list/PlanningDateGroup"
import PlanningListFilterBar from "./list/PlanningListFilterBar"
import PlanningListStats from "./list/PlanningListStats"
import { usePlanningShiftCover } from "./list/usePlanningShiftCover"
import { useRouteMetrics } from "./list/useRouteMetrics"

interface PlanningListViewProps {
  onNavigateToEdit?: (date: Date, routeId: string, editMode?: "instance" | "planned") => void
}

const PlanningListView: React.FC<PlanningListViewProps> = ({ onNavigateToEdit }) => {
  const { can, user, isPartner } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(new Date()), // Include today to match monitoring view
    to: addDays(new Date(), 14),
  })
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  /** Empty means every kind, matching how statusFilter reads. */
  const [kindFilter, setKindFilter] = useState<RouteKind[]>([])
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())
  const [expandedRouteRows, setExpandedRouteRows] = useState<Set<string>>(new Set())
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  const { data: routesPlannedData, isLoading } = useRoutesPlanned()
  const { data: locationsData, isLoading: locationsLoading } = useLocations()
  const { data: vehicleTypesData, isLoading: vehicleTypesLoading } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()

  // Route instances carry the volume forecasts (same edge function as the Timeline view)
  const startDateStr = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined
  const endDateStr = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined
  const { data: routeInstancesData, isLoading: routeInstancesLoading } =
    useRouteInstancesForDateRange(startDateStr, endDateStr)

  const rotaHorizonDays =
    (configData?.data as { shift_rota_horizon_days?: number } | undefined)
      ?.shift_rota_horizon_days ?? 7
  const {
    isLoading: shiftExceptionsLoading,
    coverFor,
    coveredFor,
    notCheckedReason,
  } = usePlanningShiftCover({ from: startDateStr, to: endDateStr, rotaHorizonDays })

  // Load factor needs the vehicle types and the locations; shift cover needs
  // the instances (to know which row is which) and the check itself. Until
  // those arrive a cell shows a spinner rather than "-" or a blank, both of
  // which are real answers and would be wrong ones for a moment.
  const loadFactorLoading = vehicleTypesLoading || locationsLoading
  const coverLoading = routeInstancesLoading || shiftExceptionsLoading
  // An export is each day's own figures -- its times, who is on it, what it is forecast to carry
  // -- and those arrive with the instances, a date at a time, well after the plan has drawn.
  // Taken before then a file has the plan's times and no volume at all, with nothing on it to
  // say so, so the exports wait.
  const exportsLoading = routeInstancesLoading || loadFactorLoading

  const locations = useMemo(() => locationsData?.data ?? [], [locationsData])

  const getLocationById = useCallback(
    (locationId: string): Location | undefined => {
      return locations.find((l) => l.id === locationId)
    },
    [locations],
  )

  const routes: PlannedRoute[] = useMemo(
    () => (routesPlannedData?.data ?? []).map(toPlannedRoute),
    [routesPlannedData],
  )

  const expandedRoutes = useMemo(
    () => expandPlannedRoutes(routes, dateRange, routeInstancesData),
    [routes, dateRange, routeInstancesData],
  )

  const metricsFor = useRouteMetrics({
    routes: expandedRoutes,
    routeInstances: routeInstancesData,
    vehicleTypes: vehicleTypesData?.data,
    config: configData?.data,
    locations: locationsData?.data,
    getLocationById,
  })

  const filteredRoutes = useMemo(() => {
    return expandedRoutes.filter((route) => {
      // A partner's plan includes routes handed to it for some dates; only those dates are its.
      if (isPartner && route.operator !== user?.operator) return false

      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const matchesSearch =
          route.code.toLowerCase().includes(term) ||
          (route.driverName?.toLowerCase().includes(term) ?? false) ||
          (route.vehicleReg?.toLowerCase().includes(term) ?? false) ||
          route.depot.toLowerCase().includes(term) ||
          (route.operator?.toLowerCase().includes(term) ?? false) ||
          route.stops.some(
            (s) =>
              s.name.toLowerCase().includes(term) ||
              (s.address?.toLowerCase().includes(term) ?? false) ||
              (s.postcode?.toLowerCase().includes(term) ?? false),
          )
        if (!matchesSearch) return false
      }

      if (statusFilter.length > 0 && !statusFilter.includes(route.status)) {
        return false
      }

      // Kind filter. Empty means every kind, so the default view still shows the spares --
      // they are part of the day's plan and a large part of its labour cost.
      if (
        kindFilter.length > 0 &&
        !kindFilter.includes(resolveRouteKind(route.route_kind))
      ) {
        return false
      }

      return true
    })
  }, [expandedRoutes, searchTerm, statusFilter, kindFilter, isPartner, user?.operator])

  /**
   * The subset a sender cares about: routes designated Haulage, or carrying a haulage stop.
   *
   * Deliberately narrower than the timeline's "haulage applicable" filter, which also sweeps in
   * HGV routes and routes with sellable empty legs -- that question is "could we sell this
   * capacity", and the answer is a sales lead. This one is "is haulage actually planned on this
   * route", because the CSV goes out to a sender as their plan, and a route we merely hope to
   * fill has nothing on it to tell them about.
   */
  const haulageRoutes = useMemo(
    () => filteredRoutes.filter((route) => isHaulageDesignatedRoute(route)),
    [filteredRoutes],
  )

  const routesByDate = useMemo(() => {
    const grouped = new Map<string, PlannedRoute[]>()

    filteredRoutes
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .forEach((route) => {
        const dateKey = format(route.date, "yyyy-MM-dd")
        const existing = grouped.get(dateKey) || []
        grouped.set(dateKey, [...existing, route])
      })

    return grouped
  }, [filteredRoutes])

  const sortValueOf = useCallback(
    (route: PlannedRoute, column: string): string | number => {
      const metrics = metricsFor(route)
      return getRouteSortValue(
        route,
        column,
        {
          volume: metrics.volume,
          fillPercentage: metrics.capacityInfo?.totalFillPercentage,
          emptyPct: metrics.emptyLegs.emptyPct,
          coverRank: coverRank(coverFor(route), coveredFor(route)),
          costTotal: metrics.cost?.total_cost,
        },
        getLocationById,
      )
    },
    [metricsFor, getLocationById, coverFor, coveredFor],
  )

  // Routes grouped by date, with each date's routes sorted by the current column and the
  // stand-by ones held at the bottom.
  const sortedRoutesByDate = useMemo(() => {
    const next = new Map<string, PlannedRoute[]>()
    routesByDate.forEach((dateRoutes, dateKey) => {
      next.set(dateKey, sortDayRoutes(dateRoutes, sortColumn, sortDirection, sortValueOf))
    })
    return next
  }, [routesByDate, sortColumn, sortDirection, sortValueOf])

  const handleSort = useCallback((column: string) => {
    setSortColumn((prev) => {
      if (prev === column) setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
      else setSortDirection("asc")
      return column
    })
  }, [])

  const totals = useMemo(() => {
    return {
      routes: filteredRoutes.length,
      stops: filteredRoutes.reduce((acc, r) => acc + r.totalStops, 0),
      volume: filteredRoutes.reduce((acc, r) => acc + metricsFor(r).volume, 0),
      cost: filteredRoutes.reduce((acc, r) => acc + (metricsFor(r).cost?.total_cost || 0), 0),
    }
  }, [filteredRoutes, metricsFor])

  const costBreakdownTotals = useMemo(() => {
    let vehicleLease = 0
    let charging = 0
    let labour = 0
    let haulageRevenue = 0
    let totalCost = 0

    filteredRoutes.forEach((route) => {
      const cost = metricsFor(route).cost
      if (cost) {
        vehicleLease += cost.vehicle_lease_cost || 0
        charging += cost.charging_cost || 0
        labour += cost.driver_wage_cost || 0
        haulageRevenue += cost.total_haulage_delivery_revenue || 0
        totalCost += cost.total_cost || 0
      }
    })

    return { vehicleLease, charging, labour, haulageRevenue, totalCost }
  }, [filteredRoutes, metricsFor])

  const toggleDateExpanded = (dateKey: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev)
      if (next.has(dateKey)) {
        next.delete(dateKey)
      } else {
        next.add(dateKey)
      }
      return next
    })
  }

  const toggleRouteExpanded = (routeId: string) => {
    setExpandedRouteRows((prev) => {
      const next = new Set(prev)
      if (next.has(routeId)) {
        next.delete(routeId)
      } else {
        next.add(routeId)
      }
      return next
    })
  }

  const expandAll = () => {
    const allKeys = Array.from(routesByDate.keys())
    setExpandedDates(new Set(allKeys))
  }

  const collapseAll = () => {
    setExpandedDates(new Set())
    setExpandedRouteRows(new Set())
  }

  const handleExport = useCallback(() => {
    if (filteredRoutes.length === 0) {
      return
    }

    const items = filteredRoutes.map((route) => {
      const { volume, capacityInfo, emptyLegs, cost } = metricsFor(route)
      return {
        route,
        volume,
        capacityInfo,
        emptyLegs,
        cost,
        shiftCover: formatShiftCoverCell(
          coverFor(route),
          coveredFor(route),
          notCheckedReason(route)?.label,
        ),
      }
    })
    const { headers, rows } = buildPlanningListTable(items, {
      vehicleTypes: vehicleTypesData?.data,
      config: configData?.data,
      getSite: getLocationById,
      can,
    })
    downloadCsv(
      `planning_routes_${exportDateRangeSlug(dateRange)}_${filteredRoutes.length}_routes.csv`,
      buildCsv(headers, rows),
    )
  }, [
    filteredRoutes,
    metricsFor,
    coverFor,
    coveredFor,
    notCheckedReason,
    dateRange,
    getLocationById,
    vehicleTypesData,
    configData,
    can,
  ])

  /** Offer the haulage plan as a file: which route, which day, where, and when. */
  const handleExportHaulage = useCallback(() => {
    if (haulageRoutes.length === 0) {
      return
    }

    const { headers, rows } = buildHaulagePlanTable(haulageRoutes, getLocationById)
    downloadCsv(
      `haulage_plan_${exportDateRangeSlug(dateRange)}_${haulageRoutes.length}_routes.csv`,
      buildCsv(headers, rows),
    )
  }, [haulageRoutes, dateRange, getLocationById])

  /**
   * What a run list would hold, in the order the screen has the routes: by day, then by the
   * column the list is sorted on, stand-by last.
   */
  const runListRoutes = useMemo(
    () => Array.from(sortedRoutesByDate.values()).flat().filter(isRunListRoute),
    [sortedRoutesByDate],
  )

  /** Offer the run list as a PDF: each route's stops in order, for whoever is running it. */
  const handleExportRunList = useCallback(async () => {
    if (runListRoutes.length === 0) {
      return
    }

    const runList = buildRunList(runListRoutes, {
      getSite: getLocationById,
      vehicleTypes: vehicleTypesData?.data,
      ...runListVolume((route) => metricsFor(route).instance, {
        locations,
        config: configData?.data,
      }),
    })
    try {
      // Fetched on demand: the PDF library is not worth loading for everyone who opens the list.
      const { downloadRunListPdf } = await import("@/lib/runListPdf")
      downloadRunListPdf(runList, runListFilename(runList, exportDateRangeSlug(dateRange)))
    } catch {
      // Most often a tab left open across a release, asking for a file that has since moved.
      toast.error("Could not build the run list. Reload the page and try again.")
    }
  }, [
    runListRoutes,
    dateRange,
    getLocationById,
    vehicleTypesData,
    metricsFor,
    locations,
    configData,
  ])

  const handleEditRoute = (
    route: PlannedRoute,
    e: React.MouseEvent,
    editMode: "instance" | "planned" = "planned",
  ) => {
    e.stopPropagation()
    if (onNavigateToEdit) {
      // For expanded recurring routes, the ID has format "originalId-YYYY-MM-DD"
      // Check if the last 10 characters match date format
      const lastPart = route.id.slice(-10)
      const isExpandedId = /^\d{4}-\d{2}-\d{2}$/.test(lastPart)
      const originalId = isExpandedId
        ? route.id.slice(0, -11) // Remove "-YYYY-MM-DD" suffix
        : route.id
      onNavigateToEdit(route.date, originalId, editMode)
    }
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-palette-jungleGreen000">
      <PlanningListStats totals={totals} />

      <PlanningListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        kindFilter={kindFilter}
        onKindFilterChange={setKindFilter}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
        routeCount={filteredRoutes.length}
        haulageRouteCount={haulageRoutes.length}
        runListRouteCount={runListRoutes.length}
        exportsLoading={exportsLoading}
        onExport={handleExport}
        onExportHaulage={handleExportHaulage}
        onExportRunList={handleExportRunList}
      />

      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <LoadingState label="Loading planned routes..." className="h-full" />
        ) : routesByDate.size === 0 ? (
          <EmptyState icon={Truck} className="flex h-full flex-col items-center justify-center">
            {routes.length === 0
              ? "No planned routes found in the database"
              : "No planned routes found for the selected criteria"}
          </EmptyState>
        ) : (
          <div className="space-y-3">
            {Array.from(sortedRoutesByDate.entries()).map(([dateKey, dateRoutes]) => (
              <PlanningDateGroup
                key={dateKey}
                dateKey={dateKey}
                dateRoutes={dateRoutes}
                isExpanded={expandedDates.has(dateKey)}
                onToggle={() => toggleDateExpanded(dateKey)}
                expandedRouteRows={expandedRouteRows}
                onToggleRoute={toggleRouteExpanded}
                onEditRoute={handleEditRoute}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                metricsFor={metricsFor}
                getLocationById={getLocationById}
                loadFactorLoading={loadFactorLoading}
                coverLoading={coverLoading}
                coverFor={coverFor}
                coveredFor={coveredFor}
                notCheckedReason={notCheckedReason}
              />
            ))}

            {can.costs && filteredRoutes.length > 0 && (
              <div className="bg-white rounded-lg border border-palette-jungleGreen200 overflow-hidden">
                <div className="p-4">
                  <CostBreakdownCard
                    density="regular"
                    title="Total Cost Breakdown"
                    subtitle={
                      `${filteredRoutes.length} route${filteredRoutes.length !== 1 ? "s" : ""} ` +
                      `across ${routesByDate.size} day${routesByDate.size !== 1 ? "s" : ""}`
                    }
                    lines={[
                      { kind: "lease", label: "Vehicle", value: costBreakdownTotals.vehicleLease },
                      { kind: "charging", label: "Charging", value: costBreakdownTotals.charging },
                      { kind: "wage", label: "Labour", value: costBreakdownTotals.labour },
                    ]}
                    total={costBreakdownTotals.totalCost}
                    revenue={costBreakdownTotals.haulageRevenue}
                    revenueLabel="Haulage Revenue"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanningListView
